import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { GetAppProvider } from "@/context/GetAppContext";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";

// Core pages (eager-loaded — on the critical path)
import LandingPage from "@/pages/LandingPage";
import Destinations from "@/pages/Destinations";
import NotFound from "@/pages/NotFound";

// Lazy-loaded pages
const SpeciesGuide = lazy(() => import("@/pages/SpeciesGuide"));
const GearChecklist = lazy(() => import("@/pages/GearChecklist"));
const AIFishingGuide = lazy(() => import("@/pages/AIFishingGuide"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const BlogAdmin = lazy(() => import("@/pages/BlogAdmin"));
const BlogEditor = lazy(() => import("@/pages/BlogEditor"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const BlogPreview = lazy(() => import("@/pages/BlogPreview"));
const Blog = lazy(() => import("@/pages/Blog"));
const AccessPointsAdmin = lazy(() => import("@/pages/AccessPointsAdmin"));
const AccessPointResearch = lazy(() => import("@/pages/AccessPointResearch"));
const DestinationsAdmin = lazy(() => import("@/pages/DestinationsAdmin"));
const SubscriptionsAdmin = lazy(() => import("@/pages/SubscriptionsAdmin"));
const MigrationDashboard = lazy(() => import("@/pages/MigrationDashboard"));
const CodeQualityDashboard = lazy(() => import("@/pages/CodeQualityDashboard"));
const DestinationDetail = lazy(() => import("@/pages/DestinationDetail"));
const EmbedMap = lazy(() => import("@/pages/EmbedMap"));
const GetAppPage = lazy(() => import("@/pages/GetAppPage"));
const DeleteAccountPage = lazy(() => import("@/pages/DeleteAccountPage"));
const BillingUpdatedPage = lazy(() => import("@/pages/BillingUpdatedPage"));

// Components
import OfflineIndicator from "@/components/OfflineIndicator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// For production custom domain, use same-origin API calls
// This works because the backend is served from the same domain with /api prefix
const getApiUrl = () => {
  // If we're on the preview domain or localhost, use the env variable
  if (window.location.hostname.includes('preview.emergentagent.com') || 
      window.location.hostname === 'localhost') {
    return `${BACKEND_URL}/api`;
  }
  // For production custom domains, use relative URL (same origin)
  return '/api';
};

export const API = getApiUrl();

// Old account, checkout and saved-items pages: the website has no accounts, so send visitors to the app page
const RETIRED_PATHS = [
  "/login", "/forgot-password", "/pricing", "/account", "/dashboard",
  "/subscription/success", "/subscription/cancel", "/auth/callback",
  "/favorites", "/saved-guides", "/saved-guides/:guideId", "/knowledge-base",
];

function AppRouter() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/destinations" element={<Destinations />} />
      <Route path="/destinations/:slug" element={<DestinationDetail />} />
      <Route path="/species" element={<SpeciesGuide />} />
      <Route path="/gear" element={<GearChecklist />} />
      <Route path="/ai-guide" element={<AIFishingGuide />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/blog" element={<BlogAdmin />} />
      <Route path="/admin/blog/new" element={<BlogEditor />} />
      <Route path="/admin/blog/edit/:articleId" element={<BlogEditor />} />
      <Route path="/admin/blog/preview/:stateSlug/:articleSlug" element={<BlogPreview />} />
      <Route path="/admin/access-points" element={<AccessPointsAdmin />} />
      <Route path="/admin/access-point-research" element={<AccessPointResearch />} />
      <Route path="/admin/destinations" element={<DestinationsAdmin />} />
      <Route path="/admin/subscriptions" element={<SubscriptionsAdmin />} />
      <Route path="/admin/migration" element={<MigrationDashboard />} />
      <Route path="/admin/code-quality" element={<CodeQualityDashboard />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/app" element={<GetAppPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/billing-updated" element={<BillingUpdatedPage />} />
      {RETIRED_PATHS.map((path) => <Route key={path} path={path} element={<Navigate to="/app" replace />} />)}
      
      {/* Embeddable Map - for WordPress and external sites */}
      <Route path="/embed/map/:slug" element={<EmbedMap />} />
      <Route path="/embed/map" element={<EmbedMap />} />
      
      {/* Public Blog Article - must be last to avoid conflicts with other routes */}
      <Route path="/:stateSlug/:articleSlug" element={<BlogArticle />} />
      
      {/* 404 Not Found - catch all unmatched routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="min-h-screen bg-background">
        <div className="grain-overlay" />
        <BrowserRouter>
          <GetAppProvider>
            <AppRouter />
          </GetAppProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" />
        <OfflineIndicator />
      </div>
    </HelmetProvider>
  );
}

export default App;
